import { useEffect, useRef, useState } from 'react'
import Styles from './V4ChannelSelection.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { TopBar } from '../TopBar/TopBar'
import { V4StepFooter } from '../V4StepFooter'
import { useStepAction } from '../useStepAction'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { useUpdateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BrandPlanTactic, ContentCount, WeekDay } from 'types'
import { Instagram, Facebook, LinkedIn, Twitter, TikTok, YouTube, Google, File02 } from 'blaze-ui/icons/20'
import { Email } from 'blaze-ui/icons/24'
import cx from 'classnames'
import { PostDaysRadioGroup } from 'screens/VirtualMarketerOnboarding/CustomizeSchedule/PostDaysRadioGroup'
import { ONLY_WEEK_DAYS } from 'screens/VirtualMarketerOnboarding/CustomizeSchedule/CustomizeSchedule'

interface ChannelDef {
  id: string
  label: string
  icon: React.ReactNode
  tactic: BrandPlanTactic | null
}

const FEED_CHANNELS: ChannelDef[] = [
  { id: 'ig-feed', label: 'Instagram', icon: <Instagram size={20} />, tactic: BrandPlanTactic.Instagram },
  { id: 'fb-feed', label: 'Facebook', icon: <Facebook size={20} />, tactic: BrandPlanTactic.Facebook },
  { id: 'li-feed', label: 'LinkedIn', icon: <LinkedIn size={20} />, tactic: BrandPlanTactic.LinkedIn },
  { id: 'tw-feed', label: 'X (Twitter)', icon: <Twitter size={20} />, tactic: BrandPlanTactic.Twitter },
  {
    id: 'gbp-feed',
    label: 'Google Business Profile',
    icon: <Google size={20} />,
    tactic: BrandPlanTactic.GoogleBusinessProfile,
  },
]

const STORY_CHANNELS: ChannelDef[] = [
  { id: 'ig-story', label: 'Instagram', icon: <Instagram size={20} />, tactic: null },
  { id: 'fb-story', label: 'Facebook', icon: <Facebook size={20} />, tactic: null },
]

const VIDEO_CHANNELS: ChannelDef[] = [
  { id: 'ig-reel', label: 'Instagram Reels', icon: <Instagram size={20} />, tactic: BrandPlanTactic.InstagramReel },
  { id: 'tiktok', label: 'TikTok', icon: <TikTok size={20} />, tactic: BrandPlanTactic.TikTok },
  { id: 'youtube', label: 'YouTube', icon: <YouTube size={20} />, tactic: BrandPlanTactic.YouTube },
]

const LONGFORM_CHANNELS: ChannelDef[] = [
  { id: 'blog', label: 'Blog', icon: <File02 size={20} />, tactic: BrandPlanTactic.SEO },
  { id: 'newsletter', label: 'Newsletter', icon: <Email size={20} />, tactic: BrandPlanTactic.Email },
]

function initSelectedFromSchedule(schedule: {
  tactics: BrandPlanTactic[]
  instagram_contents?: ContentCount[]
  facebook_contents?: ContentCount[]
}): Set<string> {
  const tactics = new Set(schedule.tactics)
  const selected = new Set<string>()

  for (const ch of FEED_CHANNELS) {
    if (ch.tactic && tactics.has(ch.tactic)) selected.add(ch.id)
  }
  for (const ch of VIDEO_CHANNELS) {
    if (ch.tactic && tactics.has(ch.tactic)) selected.add(ch.id)
  }
  for (const ch of LONGFORM_CHANNELS) {
    if (ch.tactic && tactics.has(ch.tactic)) selected.add(ch.id)
  }

  const igStoryCount = schedule.instagram_contents?.find((c) => c.type === 'story')?.count ?? 0
  if (igStoryCount > 0) selected.add('ig-story')

  const fbStoryCount = schedule.facebook_contents?.find((c) => c.type === 'story')?.count ?? 0
  if (fbStoryCount > 0) selected.add('fb-story')

  return selected
}

function buildTactics(selected: Set<string>): BrandPlanTactic[] {
  const tactics = new Set<BrandPlanTactic>()

  for (const group of [FEED_CHANNELS, VIDEO_CHANNELS, LONGFORM_CHANNELS]) {
    for (const ch of group) {
      if (ch.tactic && selected.has(ch.id)) tactics.add(ch.tactic)
    }
  }

  if (selected.has('ig-story')) tactics.add(BrandPlanTactic.Instagram)
  if (selected.has('fb-story')) tactics.add(BrandPlanTactic.Facebook)

  return [...tactics]
}

function updateStoryCount(contents: ContentCount[], type: string, count: number): ContentCount[] {
  const has = contents.some((c) => c.type === type)
  if (has) return contents.map((c) => (c.type === type ? { ...c, count } : c))
  return [...contents, { type, count }]
}

function ensureMinCount(contents: ContentCount[], min = 1): ContentCount[] {
  return contents.map((c) => ({ ...c, count: Math.max(c.count, min) }))
}

function zeroOut(contents: ContentCount[]): ContentCount[] {
  return contents.map((c) => ({ ...c, count: 0 }))
}

interface Props {
  scheduleId: number
  brandPlanId: number
  onNext: () => void
  onBack: () => void
}

export const V4ChannelSelection = ({ scheduleId, brandPlanId, onNext, onBack }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const { data: schedule, isLoading } = useBrandPlanSchedule(scheduleId)
  const { mutateAsync: updateSchedule } = useUpdateBrandPlanSchedule({ id: scheduleId })
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlanId })

  const [initialized, setInitialized] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [weekDays, setWeekDays] = useState<WeekDay[]>(ONLY_WEEK_DAYS)
  const { isSaving, run } = useStepAction()

  const snapshotRef = useRef({
    crosspost: [] as ContentCount[],
    videoXpost: [] as ContentCount[],
    seo: [] as ContentCount[],
    email: [] as ContentCount[],
    instagram: [] as ContentCount[],
    facebook: [] as ContentCount[],
  })

  useEffect(() => {
    if (!schedule || initialized) return
    setInitialized(true)
    setSelected(initSelectedFromSchedule(schedule))
    setWeekDays(schedule.week_days?.length ? schedule.week_days : ONLY_WEEK_DAYS)
    snapshotRef.current = {
      crosspost: schedule.crosspost_contents ?? [],
      videoXpost: schedule.video_crosspost_contents ?? [],
      seo: schedule.seo_contents ?? [],
      email: schedule.email_contents ?? [],
      instagram: schedule.instagram_contents ?? [],
      facebook: schedule.facebook_contents ?? [],
    }
  }, [schedule, initialized])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const anyFeedSelected = FEED_CHANNELS.some((ch) => selected.has(ch.id))
  const anyVideoSelected = VIDEO_CHANNELS.some((ch) => selected.has(ch.id))

  const handleNext = () =>
    run(async () => {
      if (!schedule) return
      const tactics = buildTactics(selected)
      const { crosspost, videoXpost, seo, email, instagram, facebook } = snapshotRef.current

      const existingIgStoryCount = instagram.find((c) => c.type === 'story')?.count ?? 0
      const igContents = updateStoryCount(
        instagram,
        'story',
        selected.has('ig-story') ? Math.max(existingIgStoryCount, 1) : 0,
      )

      const existingFbStoryCount = facebook.find((c) => c.type === 'story')?.count ?? 0
      const fbContents = updateStoryCount(
        facebook,
        'story',
        selected.has('fb-story') ? Math.max(existingFbStoryCount, 1) : 0,
      )

      const hasBlog = selected.has('blog')
      const hasNewsletter = selected.has('newsletter')

      const updatePayload = {
        tactics,
        crosspost_contents: anyFeedSelected ? ensureMinCount(crosspost) : zeroOut(crosspost),
        video_crosspost_contents: anyVideoSelected ? ensureMinCount(videoXpost) : zeroOut(videoXpost),
        seo_contents: hasBlog ? ensureMinCount(seo) : zeroOut(seo),
        email_contents: hasNewsletter ? ensureMinCount(email) : zeroOut(email),
        instagram_contents: igContents,
        facebook_contents: fbContents,
        week_days: weekDays,
      }
      await updateSchedule(updatePayload)

      if (hasBlog || hasNewsletter) {
        await updateBrandPlan({
          ...(hasBlog ? { selected_blog_platform: 'generic' } : {}),
          ...(hasNewsletter ? { selected_email_platform: 'generic' } : {}),
        })
      }

      onNext()
    }, 'Something went wrong saving your channel selection. Please try again.')

  if (isLoading || !schedule || !initialized) return null

  return (
    <MultiStepStepLayout headingLevel={isMobile ? 2 : 1} scrollContainerClassName={Styles.scrollContainer}>
      <V4StepFooter onBack={onBack} onNext={handleNext} isSaving={isSaving}>
        <div className={Styles.content}>
          <TopBar highlightStep="campaign" />

          <div className={Styles.mainRow}>
            <div className={Styles.left}>
              <div className={Styles.headerSection}>
                <div className={Styles.heading}>
                  <Heading level={isMobile ? 2 : 1}>Where and when should your content go out?</Heading>
                </div>
                <Text color="var(--dark-60)">
                  Pick your channels and set your posting scheduling preferences. You can change them at anytime.
                </Text>
              </div>

              <div className={Styles.groups}>
                <ChannelGroup
                  label="Social Feed-Posts"
                  channels={FEED_CHANNELS}
                  selected={selected}
                  onToggle={toggle}
                />
                <ChannelGroup label="Stories" channels={STORY_CHANNELS} selected={selected} onToggle={toggle} />
                <ChannelGroup
                  label="Short-Form Video"
                  channels={VIDEO_CHANNELS}
                  selected={selected}
                  onToggle={toggle}
                />
                <ChannelGroup
                  label="Long-Form & Email"
                  channels={LONGFORM_CHANNELS}
                  selected={selected}
                  onToggle={toggle}
                />

                <div className={Styles.group}>
                  <p className={Styles.groupLabel}>Post Schedule Settings</p>
                  <PostDaysRadioGroup value={weekDays} onChange={setWeekDays} className={Styles.schedulePills} />
                </div>
              </div>
            </div>

            {!isMobile && (
              <aside className={Styles.sidebar}>
                <p className={Styles.sidebarKicker}>How distribution works</p>
                <div className={Styles.sidebarTextBlock}>
                  <p className={Styles.sidebarHeadline}>Cross-posting is on by default.</p>
                  <p className={Styles.sidebarBody}>
                    Each piece of content posts across all selected channels in its group.
                  </p>
                </div>
                <hr className={Styles.sidebarDivider} />
                <p className={Styles.sidebarFooter}>
                  You can turn cross-posting off per campaign for more individual control over each channel.
                </p>
              </aside>
            )}
          </div>
        </div>
      </V4StepFooter>
    </MultiStepStepLayout>
  )
}

interface ChannelGroupProps {
  label: string
  channels: ChannelDef[]
  selected: Set<string>
  onToggle: (id: string) => void
}

function ChannelGroup({ label, channels, selected, onToggle }: ChannelGroupProps) {
  return (
    <div className={Styles.group}>
      <p className={Styles.groupLabel}>{label}</p>
      <div className={Styles.pills}>
        {channels.map((ch) => (
          <ChannelPill key={ch.id} channel={ch} isSelected={selected.has(ch.id)} onToggle={onToggle} />
        ))}
      </div>
    </div>
  )
}

interface ChannelPillProps {
  channel: ChannelDef
  isSelected: boolean
  onToggle: (id: string) => void
}

function ChannelPill({ channel, isSelected, onToggle }: ChannelPillProps) {
  return (
    <button
      type="button"
      className={cx(Styles.pill, { [Styles.pillSelected]: isSelected })}
      onClick={() => onToggle(channel.id)}
      aria-pressed={isSelected}
    >
      <span className={Styles.pillIcon}>{channel.icon}</span>
      <span className={Styles.pillLabel}>{channel.label}</span>
      {isSelected && (
        <span className={Styles.pillCheck}>
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4 8.43233C4.79279 9.0089 6.37838 10.5945 7.02703 11.6756C7.81982 9.94584 9.83784 6.05395 12 4.32422"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </button>
  )
}
